import React, { useState, useEffect, useRef } from 'react';
import { CATEGORIES, Category } from '../data';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Search, Shield, Key, BrainCircuit, Activity, Globe, Award, 
  Sparkles, Server, Cpu, Database, Link2, Lock, Coins, Scale, 
  FileText, Mail, Zap, ListTodo, HeartHandshake, ChevronRight, 
  Terminal, Settings, CheckCircle2, ChevronDown, ChevronUp
} from 'lucide-react';

interface CategoryNavigationSidebarProps {
  activeCategoryId: number;
  onSelectCategory: (id: number) => void;
  onNavigateHome?: () => void;
}

export function CategoryNavigationSidebar({ 
  activeCategoryId, 
  onSelectCategory,
  onNavigateHome
}: CategoryNavigationSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  
  // Encompass all 111 categories
  const sub27Categories = CATEGORIES;

  // Icon mapping matching categories
  const iconList = [
    Shield, Key, BrainCircuit, Activity, Globe, Award, 
    Sparkles, Server, Cpu, Database, Link2, Lock, 
    Coins, Scale, FileText, Mail, Zap, ListTodo, 
    HeartHandshake, ChevronRight, Terminal, Settings
  ];

  // Colors based on category indexes
  const colorList = [
    'text-cyan-400 group-hover:text-cyan-300',
    'text-emerald-400 group-hover:text-emerald-300',
    'text-fuchsia-400 group-hover:text-fuchsia-300',
    'text-amber-400 group-hover:text-amber-300',
    'text-indigo-400 group-hover:text-indigo-300',
    'text-rose-400 group-hover:text-rose-300',
    'text-sky-400 group-hover:text-sky-300'
  ];

  const filteredCategories = sub27Categories.filter(cat => 
    cat.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
    `cat-${cat.id}`.includes(searchQuery.toLowerCase()) ||
    `sec-${cat.id}`.includes(searchQuery.toLowerCase()) ||
    cat.nodes.some(node => node.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Synchronize highlightedIndex with activeCategoryId on mount/change
  useEffect(() => {
    const idx = filteredCategories.findIndex(c => c.id === activeCategoryId);
    if (idx !== -1) {
      setHighlightedIndex(idx);
    }
  }, [activeCategoryId, filteredCategories.length]);

  // Reset highlightedIndex when search query updates
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery]);

  // Autoscroll highlighted item in Side bar list
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const highlightedEl = scrollContainerRef.current.querySelector('[data-highlighted="true"]') as HTMLElement;
    if (highlightedEl) {
      highlightedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Handle local keydown events inside the sidebar search input
  const handleSidebarInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filteredCategories.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev >= filteredCategories.length - 1 ? 0 : prev + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? filteredCategories.length - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const targetCategory = filteredCategories[highlightedIndex];
      if (targetCategory) {
        onSelectCategory(targetCategory.id);
      }
    }
  };

  // Setup global keyboard listening protocol
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA';
      
      // Focus input when user presses '/'
      if (e.key === '/' && !isInput) {
        e.preventDefault();
        setIsCollapsed(false);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
        return;
      }

      if (isInput) return; // Let input handle onKeyDown locally
      if (filteredCategories.length === 0 || isCollapsed) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev => (prev >= filteredCategories.length - 1 ? 0 : prev + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev => (prev <= 0 ? filteredCategories.length - 1 : prev - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const targetCategory = filteredCategories[highlightedIndex];
        if (targetCategory) {
          onSelectCategory(targetCategory.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredCategories, isCollapsed, highlightedIndex, onSelectCategory]);

  return (
    <div 
      id="category-navigation-sidebar-container"
      className={`glass-panel border-2 border-indigo-500/20 rounded-2xl bg-black/90 flex flex-col transition-all duration-300 select-none overflow-hidden h-full ${
        isCollapsed ? 'w-20' : 'w-full lg:w-80'
      }`}
    >
      {/* SIDEBAR TITLE RIBBON */}
      <div className="p-4 border-b border-white/5 flex items-center justify-between bg-gradient-to-r from-indigo-950/20 to-transparent shrink-0">
        {!isCollapsed && (
          <div className="flex flex-col text-left">
            <span className="text-cyan-400 font-mono text-[8px] uppercase tracking-[0.3em] font-black">
              SEC-GRID-C2 // MATRIX
            </span>
            <span className="text-xs font-black font-sans text-white tracking-widest uppercase">
              102 DIRECTORY
            </span>
          </div>
        )}
        {isCollapsed && (
          <div className="flex items-center gap-1 mx-auto">
            <span className="text-[10px] font-mono font-black text-cyan-400 animate-pulse mr-1">
              C-102
            </span>
            <button 
              onClick={() => {
                setIsCollapsed(false);
                setTimeout(() => {
                  inputRef.current?.focus();
                }, 100);
              }}
              className="p-1 rounded border border-cyan-500/30 hover:border-cyan-400 hover:bg-cyan-950/20 bg-black/60 transition-all cursor-pointer flex items-center justify-center shrink-0"
              title="Search categories (Press '/')"
            >
              <Search className="w-3 h-3 text-cyan-400" />
            </button>
          </div>
        )}
        
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1 px-2 rounded border border-white/10 font-mono text-[8px] text-slate-400 hover:text-white hover:border-white/35 hover:bg-white/5 transition-all cursor-pointer"
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {isCollapsed ? "[>>]" : "[<<]"}
        </button>
      </div>

      {/* QUICK SECTOR FILTER SEARCH */}
      {!isCollapsed && (
        <div className="p-3 border-b border-white/5 bg-black/40 shrink-0">
          <div className="relative w-full bg-black border border-white/15 rounded-lg flex items-center shadow-inner">
            <div className="pl-2.5">
              <Search className="w-3.5 h-3.5 text-slate-500" />
            </div>
            <input 
              ref={inputRef}
              id="sidebar-search-input"
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleSidebarInputKeyDown}
              placeholder="Search 102 Grid... [/]"
              className="w-full bg-transparent text-slate-200 font-mono placeholder-slate-700 focus:outline-none text-[10px] py-1.5 px-2"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="pr-2 text-[9px] font-mono text-slate-500 hover:text-white"
              >
                x
              </button>
            )}
          </div>
        </div>
      )}

      {/* GRID SCROLL CONTAINER */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1 bg-black/20"
      >
        {filteredCategories.length > 0 ? (
          filteredCategories.map((cat, index) => {
            const IconComponent = iconList[index % iconList.length];
            const activeColor = colorList[index % colorList.length];
            const isActive = activeCategoryId === cat.id;
            const isHighlighted = index === highlightedIndex;

            return (
              <button
                key={cat.id}
                onClick={() => onSelectCategory(cat.id)}
                data-highlighted={isHighlighted}
                className={`w-full group rounded-xl transition-all duration-300 flex items-center justify-between p-2.5 text-left border relative cursor-pointer ${
                  isActive 
                    ? 'bg-indigo-500/10 border-indigo-500/40 shadow-[0_0_15px_rgba(99,102,241,0.15)] ring-1 ring-indigo-500/25' 
                    : isHighlighted
                      ? 'bg-white/10 border-indigo-500/50 shadow-[0_0_8px_rgba(99,102,241,0.1)] text-white font-bold'
                      : 'bg-transparent border-transparent hover:bg-white/5 hover:border-slate-800'
                }`}
              >
                {/* Active Indicator Bar */}
                {isActive && (
                  <motion.div 
                    layoutId="active-sidebar-pill" 
                    className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-indigo-500 shadow-[0_0_8px_#6366f1]" 
                  />
                )}

                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-1.5 rounded-lg shrink-0 transition-transform duration-300 group-hover:scale-110 flex items-center justify-center ${
                    isActive ? 'bg-indigo-500/20 text-indigo-400' : 'bg-black/40 text-slate-500'
                  }`}>
                    <IconComponent className={`w-4 h-4 ${isActive ? 'text-indigo-400' : activeColor}`} />
                  </div>
                  
                  {!isCollapsed && (
                    <div className="min-w-0 text-left">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[8px] font-mono text-slate-500 font-bold shrink-0">
                          {cat.id.toString().padStart(2, '0')}
                        </span>
                        <span className={`text-[10px] font-black font-sans uppercase truncate ${
                          isActive ? 'text-indigo-200' : 'text-slate-400 group-hover:text-slate-200'
                        }`}>
                          {cat.title.replace(' (CAT-14)', '')}
                        </span>
                      </div>
                      <span className="text-[7.5px] font-mono text-slate-500 uppercase block tracking-wider truncate">
                        {cat.nodes.length} Endpoints // Staged
                      </span>
                    </div>
                  )}
                </div>

                {!isCollapsed && (
                  <div className="flex items-center shrink-0">
                    <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                      isActive ? 'bg-emerald-400 animate-ping' : 'bg-indigo-500/30'
                    }`} />
                  </div>
                )}
              </button>
            );
          })
        ) : (
          <div className="text-center py-8 font-mono text-[9px] text-slate-600 uppercase">
            No Sectors Match Reference
          </div>
        )}
      </div>

      {/* PERSISTENT LEVY & PROTOCOL TELEMETRY BLOCK */}
      {!isCollapsed && (
        <div className="p-3 border-t border-white/5 bg-gradient-to-t from-indigo-950/25 to-transparent shrink-0 text-left space-y-1.5">
          <div className="flex items-center justify-between text-[8px] font-mono text-slate-500 uppercase">
            <span>Zakat Levy Split:</span>
            <span className="text-[#10b981] font-bold">1.0% NexoraX</span>
          </div>
          <div className="flex items-center justify-between text-[8px] font-mono text-slate-500 uppercase">
            <span>NexoraX Tax:</span>
            <span className="text-amber-500 font-bold">20% 10% Maintenance & Hosting Fee</span>
          </div>
          <div className="pt-2 border-t border-white/5 text-[7.5px] font-mono text-slate-600 uppercase flex items-center justify-between">
            <span>Secure Payment Provider Compliance:</span>
            <span className="text-cyan-400 font-semibold">NI: Secure Credential Stored Server-Side</span>
          </div>
          <div className="text-[7px] font-mono text-slate-600 uppercase flex items-center justify-between">
            <span>Secure Payment Provider Route:</span>
            <span className="text-emerald-400 font-semibold">Verified Payment Configuration | Secure Billing Provider</span>
          </div>
        </div>
      )}
    </div>
  );
}
